// Query-key factory for the tracking cache. Every key the tracking hooks
// (and their consumers) put into React Query is built here, so a key's
// shape exists in exactly one place — a one-character drift between a
// query key and its invalidation key silently breaks cache refresh, and
// with 30+ call sites that class of bug is otherwise invisible.
//
// Keys are runtime-identical to the inline arrays they replaced: cache
// identity and prefix-invalidation behaviour are unchanged.
//
// Key taxonomy (prefix relationships are load-bearing for invalidation):
//   ['tracking']                                             — everything
//   ['tracking', contractId, type]                           — one type's subtree
//   ['tracking', contractId, type, 'list', archived, params] — paged list
//   ['tracking', contractId, type, 'item', itemId]           — single item
//   ['tracking', contractId, type, 'flat-all', archived]     — full drain
//   ['tracking', contractId, 'deliverable', 'summary-all']   — full drain (archived included)
//   ['tracking', contractId, 'issue', 'backlink-all']        — issue back-link drain
//   ['tracking', contractId, type, 'byParent', parentId]     — parent-filtered drain
//   ['tracking', contractId, type, 'preview-occurrences', itemId, ruleKey]
//   ['tracking', contractId, 'milestone', milestoneId, 'linked-deliverables']
//   ['tracking', contractId, 'counts']                       — tab badges
//   ['tracking', 'summary']                                  — org dashboard
//   ['tracking', 'org-wide', type, params]                   — org-wide lists
//
// Kept in its own leaf module so component tests that vi.mock
//("../../hooks/useTracking") with an explicit factory don't have to stub
// the key builders too.

import type { TrackingType } from "../types";

export const trackingKeys = {
  all: ["tracking"] as const,

  type: (contractId: string, type: TrackingType) =>
    ["tracking", contractId, type] as const,

  list: (
    contractId: string,
    type: TrackingType,
    includeArchived: boolean,
    params: object,
  ) => ["tracking", contractId, type, "list", includeArchived, params] as const,

  item: (contractId: string, type: TrackingType, itemId: string) =>
    ["tracking", contractId, type, "item", itemId] as const,

  flatAll: (contractId: string, type: TrackingType, includeArchived: boolean) =>
    ["tracking", contractId, type, "flat-all", includeArchived] as const,

  summaryAll: (contractId: string) =>
    ["tracking", contractId, "deliverable", "summary-all"] as const,

  backlinkAll: (contractId: string) =>
    ["tracking", contractId, "issue", "backlink-all"] as const,

  byParent: (contractId: string, type: TrackingType, parentId: string) =>
    ["tracking", contractId, type, "byParent", parentId] as const,

  previewOccurrences: (
    contractId: string,
    type: TrackingType,
    itemId: string,
    recurrenceKey: unknown,
  ) =>
    [
      "tracking",
      contractId,
      type,
      "preview-occurrences",
      itemId,
      recurrenceKey,
    ] as const,

  linkedDeliverables: (contractId: string, milestoneId: string) =>
    [
      "tracking",
      contractId,
      "milestone",
      milestoneId,
      "linked-deliverables",
    ] as const,

  counts: (contractId: string) => ["tracking", contractId, "counts"] as const,

  summary: ["tracking", "summary"] as const,

  orgWide: (type: TrackingType, params: object) =>
    ["tracking", "org-wide", type, params] as const,

  /** Prefix matching every org-wide list variant for a type. */
  orgWideType: (type: TrackingType) => ["tracking", "org-wide", type] as const,

  /**
   * Predicate matching every linked-deliverables query for a contract,
   * regardless of milestone. Used to refresh milestone drawers after a
   * deliverable mutation (the FK lives on the deliverable row, so the
   * mutation doesn't know which milestones are affected).
   */
  isLinkedDeliverablesKey:
    (contractId: string) =>
    (key: readonly unknown[]): boolean =>
      key[0] === "tracking" &&
      key[1] === contractId &&
      key[2] === "milestone" &&
      key[4] === "linked-deliverables",
};
